import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getModules, GetModulesOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Lock, CheckCircle, Clock, ChevronRight } from 'lucide-react';

type Category = GetModulesOutputType['categories'][0];

const STATUS_STYLES: Record<string, string> = {
  'Not Started': 'border border-border text-muted-foreground',
  'In Progress': 'bg-accent/20 text-accent-foreground border border-accent/40',
  'Quiz Passed': 'bg-primary/20 text-primary border border-primary/30',
  'Awaiting Sign-off': 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
  'Signed Off': 'bg-primary text-primary-foreground',
};

const categoryIcons: Record<string, string> = {
  'Pub Induction': '🍺', 'Front of House': '🍷', 'Back of House': '🍳',
  'Food Safety & Hygiene': '🥗', 'Health, Safety & Fire': '🛡️',
  'Alcohol Law & Licensing': '⚖️', 'Management & Leadership': '👑',
};

export default function ModuleLibrary() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [active, setActive] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getModules({}).then(d => setCategories(d.categories)).finally(() => setLoading(false));
  }, [user]);

  const tabs = ['All', ...categories.map(c => c.name)];
  const visible = active === 'All' ? categories : categories.filter(c => c.name === active);

  if (loading) return (
    <div className="p-8 space-y-6">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <div className="grid grid-cols-3 gap-4">{[...Array(3)].map((_, j) => <Skeleton key={j} className="h-36" />)}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-background">
      {/* Tab Bar */}
      <div className="border-b border-border bg-card px-4 md:px-8 flex gap-0 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActive(tab)}
            className={`px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              active === tab ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6 space-y-6 md:space-y-8 pb-20 md:pb-6">
        {visible.map(cat => (
          <div key={cat.id}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">{categoryIcons[cat.name] || '📚'}</span>
              <h2 className="font-semibold text-foreground text-sm uppercase tracking-wider">{cat.name}</h2>
              {cat.requiredForAllStaff && (
                <span className="text-xs bg-primary/20 text-primary border border-primary/30 px-2 py-0.5">Required</span>
              )}
            </div>
            {cat.modules.length === 0 ? (
              <p className="text-muted-foreground text-sm">No published modules yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.modules.map(mod => (
                  <div key={mod.id} className={`border bg-card transition-colors ${mod.isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 cursor-pointer'}`}>
                    {mod.isLocked ? (
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <Lock className="w-5 h-5 text-muted-foreground" />
                          <span className={`text-xs px-2 py-0.5 ${STATUS_STYLES['Not Started']}`}>Locked</span>
                        </div>
                        <p className="font-medium text-foreground text-sm mb-1">{mod.title}</p>
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{mod.description}</p>
                        <p className="text-xs text-muted-foreground">Complete Pub Induction first</p>
                      </div>
                    ) : (
                      <Link to={`/modules/${mod.id}`} className="block p-4">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-xl">{categoryIcons[cat.name] || '📚'}</span>
                          <span className={`text-xs px-2 py-0.5 ${STATUS_STYLES[mod.traineeStatus] || STATUS_STYLES['Not Started']}`}>
                            {mod.traineeStatus === 'Not Started' ? 'Not started' : mod.traineeStatus}
                          </span>
                        </div>
                        <p className="font-medium text-foreground text-sm mb-1">{mod.title}</p>
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{mod.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {mod.estimatedDuration || '–'}</span>
                          {mod.traineeStatus === 'In Progress' ? (
                            <span className="text-xs text-primary font-medium flex items-center gap-1">Resume <ChevronRight className="w-3 h-3" /></span>
                          ) : mod.traineeStatus === 'Signed Off' || mod.traineeStatus === 'Quiz Passed' ? (
                            <CheckCircle className="w-4 h-4 text-primary" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </div>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
